import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Conversation } from '@/types'

// Chat endpoints live on Express v1 (/api) — conversations, send, etc.
const V1 = '/api'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function v1get<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1post<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export type DirectSendJob = {
  id: string
  status: 'queued' | 'running' | 'done' | 'failed'
  pageId: string
  pageName: string
  message: string
  totalContacts: number
  processed: number
  sent: number
  failed: number
  currentWave: number
  totalWaves: number
  errors: { psid: string; error: string; trace?: string }[]
  createdAt: number
  updatedAt: number
  startedAt: number | null
  completedAt: number | null
  pages: Array<{
    pageId: string
    pageName: string
    totalContacts: number
    processed: number
    sent: number
    failed: number
    currentWave: number
    totalWaves: number
    status: string
  }>
}

export function useConversations() {
  return useQuery<Record<string, Conversation>>({
    queryKey: ['conversations'],
    queryFn: () => v1get('/chat/conversations'),
    refetchInterval: 15000,
  })
}

export function useSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { conversationKey: string; text: string }) =>
      v1post('/chat/send', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  })
}

export function useToggleAutoReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationKey: string) =>
      v1post<{ ok: boolean; autoReply: boolean }>('/chat/toggle-auto', { conversationKey }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationKey: string) =>
      v1post('/chat/mark-read', { conversationKey }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  })
}

export function useSendOTNRequest() {
  return useMutation({
    mutationFn: (conversationKey: string) =>
      v1post('/chat/send-otn-request', { conversationKey }),
  })
}

export function useSendDirect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { pageId?: string; psids?: string[]; pageGroups?: { pageId: string; psids: string[] }[]; message: string; buttonText?: string; buttonUrl?: string }) =>
      v1post<{ ok: boolean; jobId: string; status: string }>('/chat/send-direct', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  })
}

export function useDirectSendStatus(jobId: string | null) {
  return useQuery<DirectSendJob>({
    queryKey: ['chat-direct-job', jobId],
    queryFn: () => v1get(`/chat/send-direct/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const state = query.state.data?.status
      return state === 'queued' || state === 'running' ? 1500 : false
    },
  })
}
